import { useIsFocused } from "expo-router/react-navigation";
import { router } from 'expo-router';
import { useLayoutEffect, useState } from 'react';

import { useNpoOnboarding } from '@/api/hooks/meHooks';
import { useUpdateNonProfitOrg } from '@/api/hooks/nonProfitOrgHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import NpoPartyAuditForm from '@/components/forms/NpoPartyAuditForm';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { ThirdPartyAuditData } from '@/lib/validations/NpoOnboardingSchema';
import { containsSection } from '@/utils/containsSection';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { safeJsonParse } from '@/utils/jsonParse';
import { FileToUpload } from '@/utils/uploadMultipleToFireStorage';
import { uploadToFireStorage } from '@/utils/uploadToFireStorage';

const PartyAudit = () => {
  const { state: authState } = useAuth();
  const { state: npoScreensState, setCurrentPage } = useNpoLoginScreenState();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useNpoOnboarding(
    authState.signUpData?.loginEmail?.type ?? 'CHURCH'
  );
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);

  const isFocused = useIsFocused();
  useLayoutEffect(() => {
    if (isFocused) setCurrentPage(8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');
  const initialData = {
    thirdPartyAudit:
      typeof nonProfitOrg?.thirdPartyAudit === 'string' ? safeJsonParseArray(nonProfitOrg.thirdPartyAudit)[0] : '',
    thirdPartyAuditDeny: nonProfitOrg?.thirdPartyAuditDeny
  };

  const updateNonProfit = ({
    thirdPartyAudit,
    thirdPartyAuditDeny
  }: {
    thirdPartyAudit: string[] | null;
    thirdPartyAuditDeny: boolean;
  }) => {
    nonProfitUpdate(
      {
        npoId: authState.signUpData?.loginEmail?.npoId ?? '',
        thirdPartyAudit: thirdPartyAudit,
        thirdPartyAuditDeny: thirdPartyAuditDeny
      },
      {
        onSuccess: () => {
          onBoardingUpdate(
            {
              preferenceId: authState.preferenceId ?? '',
              partyAudit: true
            },
            {
              onSuccess: () => {
                setIsLoading(false);
                setFileProgress(undefined);
                router.navigate('/(app)/login/nonProfit/onBoarding/impactStudy');
              }
            }
          );
        },
        onError: (e) => {
          setIsLoading(false);
          console.error(e);
        }
      }
    );
  };

  const onSubmit = async (data: ThirdPartyAuditData) => {
    try {
      setIsLoading(true);
      if (data.thirdPartyAudit && !containsSection(data.thirdPartyAudit, 'thirdParty')) {
        let fileToUpload: FileToUpload = {
          uri: data.thirdPartyAudit,
          name: `third_party_audit.${new Date().toISOString()}`
        };
        await uploadToFireStorage(
          `non-profits-orgs/${authState.signUpData?.loginEmail?.npoId}/thirdParty/`,
          fileToUpload,
          (progress) => setFileProgress(progress)
        ).then((storageFile) => {
          updateNonProfit({
            thirdPartyAudit: [storageFile.metadata.fullPath],
            thirdPartyAuditDeny: data.thirdPartyAuditDeny
          });
        });
      } else {
        if (data.thirdPartyAuditDeny) {
          updateNonProfit({
            thirdPartyAudit: null,
            thirdPartyAuditDeny: data.thirdPartyAuditDeny
          });
        } else {
          updateNonProfit({
            thirdPartyAudit: [],
            thirdPartyAuditDeny: data.thirdPartyAuditDeny
          });
        }
      }
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      console.error(e);
    }
  };

  return (
    <NpoPartyAuditForm
      initialData={initialData}
      isLoading={isLoading || onBoardingIsPending || nonProfitIsPending}
      fileProgress={fileProgress}
      onSubmitTapped={onSubmit}
    />
  );
};

export default PartyAudit;
